import {
    Database,
    Star,
    Apple,
    Download,
} from "lucide-react";
import Nav from "../comps/nav"
import {useEffect, useRef, useState} from "react";
import "../index.css"
import Feature from "../comps/feature";
import Footer from "../comps/footer";
import {Link} from "react-router-dom";
import LazyVideo from "../comps/LazyVideo";
import testPhone from '../assets/Animated_Phone.mp4';
import testPhone2 from '../assets/Animated_Phone2.mp4';

export default function Landing() {
    const [entered, setEntered] = useState(false);

    useEffect(() => {
        const id = requestAnimationFrame(() => setEntered(true));
        return () => cancelAnimationFrame(id);
    }, []);

    const [featuresVisible, setFeaturesVisible] = useState(false);
    const featuresRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const el = featuresRef.current;
        if (!el) return;

        const obs = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setFeaturesVisible(true);
                    obs.disconnect();
                }
            },
            { threshold: 0.2 }
        );

        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    return (
        <main className="w-full min-w-full min-h-screen bg-[#f2f4f5] pb-12">
            <Nav/>
            {/* Intro Section */}
            <section className="md:pt-28 pt-14 relative md:px-4">
                <div className="w-4/5 container mx-auto max-w-6xl">
                    <div className="flex md:flex-row flex-col items-center justify-between">
                        <div className="hidden md:w-auto md:flex justify-start">
                            <LazyVideo srcMp4={testPhone} poster="/phone_image1.png" className="w-80" />
                        </div>
                        <div className="md:w-1/3 mb-10 mt-10">
                            <h1 className="text-4xl md:text-5xl font-bold mb-6 font-sinkinsans">
                                Always Ready
                            </h1>
                            <p className="text-xl mb-8 text-gray-600">
                                Secure, on-demand access to your medical information.
                            </p>
                        </div>
                        <div className="flex md:w-auto md:hidden justify-start">
                            <LazyVideo srcMp4={testPhone} poster="/phone_image1.png" className="w-80" />
                        </div>
                        <div className="md:w-auto">
                            <LazyVideo srcMp4={testPhone2} poster="/phone_image2.png" className="w-80" />
                        </div>
                    </div>
                </div>
            </section>
            {/* Key Features Section */}
            <section className="pt-28  md:px-4">
                <div className="w-4/5 container mx-auto max-w-6xl">
                    <h1 className="text-4xl md:text-5xl font-bold mb-6 font-sinkinsans">
                        Built for Everyone
                    </h1>
                    <h1 className="mb-6 text-gray-600">
                        Patients & Professionals, Monolingual or Multilingual, Young & Old
                    </h1>
                    <div
                        ref={featuresRef}
                        className="flex flex-col md:grid md:grid-cols-3 gap-5"
                    >
                        <Feature
                            title="Digital Medical Wallet"
                            description="Store and access your medication details anytime, even offline"
                            featuresVisible={featuresVisible}
                            size={2}
                            src="https://lottie.host/26ae0f25-b401-433e-98be-3f7ecebdf48d/kS9ZdqA2w1.lottie"
                        />
                        <Feature
                            title="Medication Finder"
                            description="Search through a comprehensive database of medications, products, and supplements"
                            featuresVisible={featuresVisible}
                            size={1}
                            delay={300}
                            src="https://lottie.host/a1464c4b-38ba-495d-9169-ebcda2be7c24/Ckr2muOSQp.lottie"
                        />
                        <Feature
                            title="Medication Management"
                            description="Comprehensive drug information from trusted sources"
                            featuresVisible={featuresVisible}
                            delay={450}
                            size={1}
                            src="https://lottie.host/08c2b832-9834-4721-a24e-e950c2226a9f/ype99b62Kl.lottie"
                        />
                        <Feature
                            title="Document Storage"
                            description="Available in multiple languages for wider accessibility"
                            featuresVisible={featuresVisible}
                            delay={750}
                            size={1}
                            src="https://lottie.host/0ffcb4e6-8c53-40e6-ae56-c76d67d04288/KBnxzZAwdO.lottie"
                        />
                        <Feature
                            title="Language Accessibility"
                            description="Supports multiple languages"
                            featuresVisible={featuresVisible}
                            delay={900}
                            size={1}
                            src="https://lottie.host/d62b8afd-5b79-44f4-81cc-a8ea784c6a80/vxwsU6NgiH.lottie"
                        />
                    </div>
                </div>
            </section>
            {/* Trust Section */}
            <section className="py-20  md:px-4">
                <div className="container mx-auto max-w-6xl">
                    <div className="grid md:grid-cols-3 gap-8 text-center">
                        <div>
                            <div
                                className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Star className="w-10 h-10 text-blue-500"/>
                            </div>
                            {/*<h3 className="text-2xl font-bold mb-2">4.9/5</h3>*/}
                            <h3 className="text-2xl font-bold mb-2">5.0</h3>
                            <p className="text-gray-600">User Rating</p>
                        </div>
                        <div>
                            <div
                                className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Database className="w-10 h-10 text-blue-500"/>
                            </div>
                            {/*<h3 className="text-2xl font-bold mb-2">10,000+</h3>*/}
                            <h3 className="text-2xl font-bold mb-2">~140,000+</h3>
                            <p className="text-gray-600">Medications to Access</p>
                        </div>
                        <div>
                            <div
                                className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Download className="w-10 h-10 text-blue-500"/>
                            </div>
                            {/*<h3 className="text-2xl font-bold mb-2">1M+</h3>*/}
                            <h3 className="text-2xl font-bold mb-2">23 (million) soon</h3>
                            <p className="text-gray-600">App Downloads</p>
                        </div>
                    </div>
                </div>
            </section>
            {/* App Screenshots */}
            {/*<section className="py-20 px-4">*/}
            {/*    <div className="container mx-auto max-w-6xl">*/}
                    {/*<div className="flex overflow-x-auto space-x-6 pb-8 justify-center">*/}
                    {/*    {[1, 2, 3, 4].map((i) => (*/}
                    {/*        <img*/}
                    {/*            key={i}*/}
                    {/*            src={`https://placehold.co/250x500/blue/white`}*/}
                    {/*            alt={`App Screenshot ${i}`}*/}
                    {/*            className="rounded-2xl shadow-lg flex-none"*/}
                    {/*        />*/}
                    {/*    ))}*/}
                    {/*</div>*/}
            {/*    </div>*/}
            {/*</section>*/}
            {/* CTA Section */}
            <section className="py-20  md:px-4 text-center">
                <div className="container mx-auto max-w-4xl">
                    <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-6">
                        Download AMII Today
                    </h2>
                    <p className="text-xl text-blue-700 mb-8">
                        Join other users who trust AMII for their medical preparation and peace of mind.
                    </p>
                    <div className="flex justify-center space-x-4">
                        <button
                            className="flex items-center bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition">
                            <Apple className="w-6 h-6 mr-2"/>
                            <Link to="https://apps.apple.com/us/app/amii-health/id6749024239" target="_blank" rel="noopener noreferrer">App Store</Link>
                        </button>
                        {/*<button*/}
                        {/*    className="flex items-center bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition">*/}
                        {/*    <Phone className="w-6 h-6 mr-2"/>*/}
                        {/*    Play Store*/}
                        {/*</button>*/}
                    </div>
                </div>
            </section>
            <Footer/>
        </main>
    );
}
